import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  PopoverMenu,
  Menu,
  MenuItem,
  MenuTrigger,
  MenuSectionHeader
} from '@contrast-security-inc/design-system-popovers'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { VulnStatusDisplay } from './vuln-status-display'
import { Vulnerability } from './status-action'
import { Status, StatusRegistry } from './status'
import { Role } from '@contrast-security-inc/base-types'
import {
  IconDropdownCollapse,
  IconDropdownExpand
} from '@contrast-security-inc/design-system-foundations'

interface Props {
  readonly onSelectStatus: (status: Status) => void
  readonly role: Role
  readonly statusRegistry: StatusRegistry
  readonly menuPlacement: 'top' | 'bottom'
  readonly vulnerabilities: Vulnerability[]
}

export function UpdateStatusMenu({
  onSelectStatus,
  role,
  statusRegistry,
  menuPlacement,
  vulnerabilities
}: Props) {
  const { formatMessage } = useIntl()

  const menuItems = [
    <MenuItem
      key={Status.REPORTED.name}
      itemID={Status.REPORTED.name}
      testId={'menu-option-' + Status.REPORTED.name}
      onClick={() => onSelectStatus(Status.REPORTED)}
    >
      {Status.REPORTED.name}
    </MenuItem>,
    // First Section Header
    <MenuSectionHeader key="header1">
      <FormattedMessage id="title_vulnerabilityStatusOpen">
        {(text) => {
          return <>{text}</>
        }}
      </FormattedMessage>
    </MenuSectionHeader>,
    // First list of MenuItems
    ...statusRegistry.openManual
      .filter(
        (status) => status !== Status.REPORTED && status.isAllowedByRole(role)
      )
      .map((status) => (
        <MenuItem
          key={status.name}
          itemID={'menu-option-' + status.name}
          testId={'menu-option-' + status.name}
          onClick={() => onSelectStatus(status)}
        >
          {formatMessage({ id: status.i18nID })}
        </MenuItem>
      )),
    // Second Section Header
    <MenuSectionHeader key="header2">
      <FormattedMessage id="title_vulnerabilityStatusClosed">
        {(text) => {
          return <>{text}</>
        }}
      </FormattedMessage>
    </MenuSectionHeader>,
    // Second list of MenuItems
    ...statusRegistry.closedManual
      .filter(
        (status) => status !== Status.REPORTED && status.isAllowedByRole(role)
      )
      .map((status) => (
        <MenuItem
          key={status.name}
          itemID={'menu-option-' + status.name}
          testId={'menu-option-' + status.name}
          onClick={() => onSelectStatus(status)}
        >
          {formatMessage({ id: status.i18nID })}
        </MenuItem>
      ))
  ]

  return (
    <PopoverMenu placement={menuPlacement} testId="popover-menu">
      <Menu>{menuItems}</Menu>
      <MenuTrigger data-e2e="vuln-status-bulk-action">
        {({ isOpen }) => (
          <Button
            variant="secondary"
            Icon={isOpen ? IconDropdownCollapse : IconDropdownExpand}
          >
            <VulnStatusDisplay vulnerabilities={vulnerabilities} />
          </Button>
        )}
      </MenuTrigger>
    </PopoverMenu>
  )
}
